import { Badge } from '~/components/ui/badge';
import { parentStudentStatus } from '~/pages/ParentEnrollment/parentStudentStatus';

const FAMILY_STATE_CONFIG: Record<string, { label: string; variant: string }> = {
  DRAFT: { label: 'Draft', variant: 'outline' },
  READY_TO_INVITE: { label: 'Ready to Invite', variant: 'secondary' },
  INVITED: { label: 'Invited', variant: 'default' },
  IN_PROGRESS: { label: 'In Progress', variant: 'default' },
  NEEDS_REVIEW: { label: 'Needs Review', variant: 'destructive' },
  APPROVED: { label: 'Approved', variant: 'success' },
  DECLINED: { label: 'Declined', variant: 'destructive' },
};

const STUDENT_STATE_VARIANTS: Record<string, string> = {
  PENDING_FAMILY_CONFIRMATION: 'secondary',
  PENDING_ADMISSIONS_REVIEW: 'secondary',
  WAITLISTED: 'outline',
  ACCEPTED_PENDING_DEPOSIT: 'default',
  ENROLLED: 'success',
  WITHDRAWN: 'destructive',
  DENIED: 'destructive',
  NOT_RETURNING: 'outline',
};

const KIND_CONFIG: Record<string, { label: string; variant: string }> = {
  NEW_ADMISSION: { label: 'New Admission', variant: 'outline' },
  RE_ENROLLMENT: { label: 'Re-enrollment', variant: 'outline' },
  NEW_STUDENT: { label: 'New Student', variant: 'outline' },
  RETURNING_STUDENT: { label: 'Returning', variant: 'outline' },
};

const INVITE_STATUS_CONFIG: Record<string, { label: string; variant: string }> = {
  NOT_READY: { label: 'Not Ready', variant: 'outline' },
  QUEUED: { label: 'Queued', variant: 'secondary' },
  SENT: { label: 'Sent', variant: 'default' },
  CANCELED: { label: 'Canceled', variant: 'destructive' },
};

// A year opened by the checklist invite shows its access, not the old flow's state.
const ACCESS_CONFIG: Record<string, { label: string; variant: string }> = {
  NOT_INVITED: { label: 'Not invited', variant: 'outline' },
  ENROLLING: { label: 'Enrolling', variant: 'default' },
  ENROLLED: { label: 'Enrolled', variant: 'success' },
  DECLINED: { label: 'Declined', variant: 'destructive' },
};

interface EnrollmentStatusBadgeProps {
  state: string;
  /** With accessSource SET on a family year, the badge reads access instead of state. */
  access?: string | null;
  accessSource?: string | null;
  kind?: string;
  inviteStatus?: string;
  type?: 'family' | 'student';
  showKind?: boolean;
  showInviteStatus?: boolean;
}

export function EnrollmentStatusBadge({
  state,
  access,
  accessSource,
  kind,
  inviteStatus,
  type = 'family',
  showKind = false,
  showInviteStatus = false,
}: EnrollmentStatusBadgeProps) {
  const familyInfo =
    accessSource === 'SET' && access
      ? ACCESS_CONFIG[access] || { label: access, variant: 'outline' }
      : FAMILY_STATE_CONFIG[state] || { label: state, variant: 'outline' };
  // A child's status reads the same words as the office's status menu.
  const stateInfo =
    type === 'family'
      ? familyInfo
      : { label: STUDENT_STATE_VARIANTS[state] ? parentStudentStatus(state) : state, variant: STUDENT_STATE_VARIANTS[state] ?? 'outline' };
  const kindInfo = kind ? KIND_CONFIG[kind] : undefined;
  const inviteInfo = inviteStatus ? INVITE_STATUS_CONFIG[inviteStatus] : undefined;

  return (
    <div className="flex flex-wrap gap-1">
      <Badge variant={stateInfo.variant as "outline" | "secondary" | "default" | "destructive"}>
        {stateInfo.label}
      </Badge>
      {showKind && kindInfo && (
        <Badge variant={kindInfo.variant as "outline" | "secondary" | "default" | "destructive"}>
          {kindInfo.label}
        </Badge>
      )}
      {showInviteStatus && inviteInfo && inviteStatus !== 'NOT_READY' && (
        <Badge variant={inviteInfo.variant as "outline" | "secondary" | "default" | "destructive"}>
          Invite: {inviteInfo.label}
        </Badge>
      )}
    </div>
  );
}

export default EnrollmentStatusBadge;
